import { Component } from "react";
import { Link } from "react-router-dom";

/**
 * ErrorBoundary — catches any runtime render error in the React tree so a
 * single component throw can never white-screen the whole SPA. Renders a
 * calm fallback UI that keeps the header/back-to-home link intact.
 */
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Surface the crash in the browser console for RCA. Do not swallow it
    // silently — the console trail is critical for diagnosing prod.
    // eslint-disable-next-line no-console
    console.error("[Zynthoro] Unhandled render error:", error, info?.componentStack);
  }

  handleReload = () => {
    // Full reload AT A SAFE ROUTE — never reload the same URL that just
    // crashed, otherwise the user can get stuck in a reload loop on a
    // persistently-broken page. If the user was in the dashboard, send
    // them to /dashboard; else to the landing page.
    try {
      const path = window.location.pathname || "";
      const safeHref = path.startsWith("/dashboard") ? "/dashboard" : "/";
      window.location.assign(safeHref);
    } catch {
      window.location.reload();
    }
  };

  handleHome = () => {
    // Clear the error state before the <Link> navigates so the fresh
    // route renders instead of staying stuck on this fallback.
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div
        role="alert"
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "24px",
          background: "#ffffff",
          fontFamily:
            "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
        }}
        data-testid="app-error-boundary"
      >
        <div style={{ maxWidth: 480, textAlign: "left" }}>
          <p
            style={{
              fontSize: 11.5,
              fontWeight: 600,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: "#1A4FFF",
              marginBottom: 12,
            }}
          >
            Zynthoro
          </p>
          <h1
            style={{
              fontSize: 28,
              lineHeight: 1.2,
              color: "#0A1628",
              fontWeight: 700,
              margin: 0,
            }}
          >
            Something went wrong.
          </h1>
          <p style={{ marginTop: 16, color: "#4a5568", lineHeight: 1.6, fontSize: 15 }}>
            The page didn&apos;t load correctly. Please try refreshing — if it keeps
            happening, drop us a line at{" "}
            <a href="mailto:support@zynthoro.ai" style={{ color: "#1A4FFF" }}>
              support@zynthoro.ai
            </a>
            .
          </p>
          <div style={{ marginTop: 28, display: "flex", gap: 12, flexWrap: "wrap" }}>
            <button
              type="button"
              onClick={this.handleReload}
              data-testid="error-boundary-reload"
              style={{
                background: "#1A4FFF",
                color: "#fff",
                border: 0,
                padding: "10px 20px",
                borderRadius: 8,
                fontSize: 14,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Reload the page
            </button>
            <Link
              to="/"
              data-testid="error-boundary-home"
              onClick={this.handleHome}
              style={{
                background: "#fff",
                color: "#0A1628",
                border: "1px solid #eef1f7",
                padding: "10px 20px",
                borderRadius: 8,
                fontSize: 14,
                fontWeight: 600,
                textDecoration: "none",
                display: "inline-flex",
                alignItems: "center",
              }}
            >
              Back to home
            </Link>
          </div>
        </div>
      </div>
    );
  }
}
